import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useDrop } from 'react-dnd';

import { Theme, Colors } from '@app/theme';
import {
  Participation,
  ParticipationStatus,
  QuotaTag,
  StudyType,
  useInfiniteSessionParticipations,
  useIsAdmin,
  API_URL,
} from '@app/api';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import {
  getDragTypeFromStatus,
  getDropAcceptTypes,
  useDropMethod,
} from 'lib/participationDragAndDrop';
import { useScrollEdges } from 'hooks/useScrollEdges';
import {
  Chip,
  DotsMenu,
  IconChip,
  InfiniteLoadingContainer,
  MenuItem,
} from 'components';

import {
  DraggableParticipantMonitoringCard,
  ParticipantMonitoringCard,
} from './ParticipantMonitoringCard';
import { Identifier } from 'dnd-core';
import moment from 'moment-timezone';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  column: {
    minWidth: 280,
    maxWidth: 280,
    flex: 1,
    background: 'white',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.borderRadius.std,
    padding: [[theme.marginBase * 2, 0]],
    transition: 'box-shadow .3s, opacity .3s',
  },
  columnPurple: {
    backgroundColor: theme.colors.purple,
  },
  columnBlue: {
    backgroundColor: theme.colors.blue,
  },
  columnAcceptDrop: {
    boxShadow: theme.boxShadow.base,
  },
  columnDeclineDrop: {
    opacity: 0.5,
  },
  closingCardsBorder: {
    borderBottom: {
      style: 'solid',
      color: theme.colors.grayBorder,
      width: 1,
    },
    margin: [[0, theme.marginBase * 2]],
  },
  titleLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 2,
    padding: [[0, theme.marginBase * 2]],
  },
  titleAndChip: {
    display: 'flex',
    alignItems: 'center',
  },
  card: {
    marginBottom: theme.marginBase * 2,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  title: {
    ...theme.fonts.h5,
    textTransform: 'uppercase',
    color: theme.colors.inkSecondary,
  },
  titlePurple: {
    color: theme.colors.white,
  },
  titleBlue: {
    color: theme.colors.white,
  },
  chip: {
    marginLeft: theme.marginBase,
  },
  emptyBlock: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,

    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
  },
  buttonPurple: {
    '& > i': {
      color: theme.colors.white,
    },
    '&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)': {
      backgroundColor: theme.colors.purpleAccessible,
    },
  },
  buttonBlue: {
    '& > i': {
      color: theme.colors.white,
    },
    '&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)': {
      backgroundColor: theme.colors.blueAccessible,
    },
  },
}));

type ColorStyleKeys =
  | `${Colors}Column`
  | `${Colors}Title`
  | `${Colors}Button`
  | 'whiteColumn'
  | 'whiteTitle'
  | 'whiteButton';

const useColorsStyle = createUseStyles<ColorStyleKeys, {}, Theme>(
  (theme: Theme) =>
    Object.entries(theme.colorDefinitions).reduce(
      (acc, [color, def]) => ({
        ...acc,
        [color + 'Column']: {
          backgroundColor: def.std,
        },
        [color + 'Title']: {
          color: def.hoverAccessible,
        },
        [color + 'Button']: {
          '& > i': {
            color: theme.colors.white,
          },
          '&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)':
            {
              backgroundColor: def.accessible,
            },
        },
      }),
      {
        whiteColumn: {},
        whiteTitle: {},
        whiteButton: {},
      } as any,
    ),
);

interface Props {
  className?: string;
  status: ParticipationStatus | ParticipationStatus[];
  color?: Colors | 'white';
  onDragToCancel?: (id: string) => any;
  onDragToBook?: (id: string) => any;
  onDragFromOFQ?: (id: string) => any;
  onParticipationClick: (p: Participation) => any;
  onMenuItemClick?: (val: ParticipationStatus[], type: 'email' | 'sms') => any;
  studyType: StudyType;
  sessionId: string;
  search?: string;
  informationCard?: React.ReactNode;
  quotasPerApplication?: Map<string, QuotaTag[]>;
  lock?: boolean;
  hideWhenEmpty?: boolean;
}

export const MonitoringColumn = memo(function MonitoringColumnComp({
  className,
  status: statusProps,
  color = 'white',
  onDragToBook,
  onDragToCancel,
  onDragFromOFQ,
  onParticipationClick,
  onMenuItemClick,
  studyType,
  sessionId,
  search,
  informationCard,
  quotasPerApplication,
  lock,
  hideWhenEmpty,
}: Props) {
  const classes = useStyles();
  const colorClasses = useColorsStyle();
  const { i18n, t } = useTranslation();
  const isAdmin = useIsAdmin();

  const statusList = Array.isArray(statusProps) ? statusProps : [statusProps];

  const status = statusList[0];

  const participationsRes = useInfiniteSessionParticipations(
    sessionId,
    statusList,
    search,
  );

  const pages = participationsRes.data ? participationsRes.data.pages : [];
  const count = pages.length
    ? pages[pages.length - 1].pagination.count
    : undefined;

  const onDownloadClick = useCallback(() => {
    window.open(
      `${API_URL}/sessions/${sessionId}/participations/csv?timezone=${moment.tz.guess()}&${statusList
        .map((s) => `status[]=${s}`)
        .join('&')}`,
      '_blank',
    );
  }, [sessionId, statusList]);

  const dropMethod = useDropMethod(statusProps, studyType, {
    onDragToCancel,
    onDragToBook,
    onDragFromOFQ,
  });

  const context = studyType ? studyType.toLowerCase() : undefined;
  const containingCardTypes: Identifier[] = statusList.map(
    getDragTypeFromStatus,
  );
  const accept = getDropAcceptTypes(statusList, studyType);
  const [res, drop] = useDrop({
    accept,
    drop: (item: any) => {
      dropMethod(item);
      return { status: statusProps };
    },
    collect: (mon) => ({
      isOver: !!mon.isOver(),
      canDrop: !!mon.canDrop(),
      itemType: mon.getItemType(),
      item: mon.getItem(),
      dropResult: mon.getDropResult(),
      didDrop: mon.didDrop(),
    }),
  });

  const { itemType, item } = res;
  const droppedHere = res.didDrop && res.dropResult.status === statusProps;
  const isOver = res.isOver || droppedHere;
  const canDrop = res.canDrop || droppedHere;

  const {
    scrollAtTop,
    scrollAtBottom,
    ref: scrollComponent,
    updateScrollPosition,
  } = useScrollEdges();

  useEffect(() => {
    setTimeout(() => {
      updateScrollPosition();
    });
  }, [pages, updateScrollPosition]);

  useEffect(() => {
    if (!isOver || !canDrop || !scrollComponent.current) {
      return;
    }
    if (itemType && containingCardTypes.includes(itemType)) {
      return;
    }

    scrollComponent.current.scrollTop = 0;
    setTimeout(() => {
      updateScrollPosition();
    });
  }, [
    isOver,
    canDrop,
    scrollComponent,
    updateScrollPosition,
    itemType,
    containingCardTypes,
  ]);

  if (!isAdmin && hideWhenEmpty && !count) {
    return null;
  }

  return (
    <div
      className={classnames(
        classes.column,
        className,
        {
          [classes.columnAcceptDrop]: canDrop,
          [classes.columnDeclineDrop]: itemType && !canDrop,
        },
        colorClasses[`${color}Column`],
      )}
      ref={drop}
    >
      <div className={classes.titleLine}>
        <div className={classes.titleAndChip}>
          <h3
            className={classnames(classes.title, colorClasses[`${color}Title`])}
          >
            <Trans
              i18nKey={`sessions.details.monitoring.columnTitle.${statusList[0]}`}
              context={context}
            />
          </h3>
          {count !== undefined && !search && (
            <Chip
              className={classes.chip}
              color={getColorForParticipationStatus(statusList[0])}
            >
              {count}
            </Chip>
          )}
          {lock && (
            <IconChip
              className={classes.chip}
              icon="ri-lock-line"
              color="red"
            />
          )}
        </div>
        <div>
          <DotsMenu className={colorClasses[`${color}Button`]}>
            <MenuItem
              label={
                status === ParticipationStatus.INVITED
                  ? t('sessions.details.monitoring.more.contactAgain')
                  : t('sessions.details.monitoring.more.sendMail')
              }
              onClick={() =>
                onMenuItemClick && onMenuItemClick(statusList, 'email')
              }
            />
            {isAdmin && (
              <MenuItem
                label={t('sessions.details.monitoring.more.sendSms')}
                onClick={() =>
                  onMenuItemClick && onMenuItemClick(statusList, 'sms')
                }
              />
            )}
            <MenuItem disabled={!pages.length} onClick={onDownloadClick}>
              {t('sessions.details.monitoring.more.downloadAnswers')}
            </MenuItem>
          </DotsMenu>
        </div>
      </div>

      {!!count && !scrollAtTop && (
        <div className={classes.closingCardsBorder} />
      )}
      <InfiniteLoadingContainer
        infiniteQueryResponse={participationsRes}
        loader={<ParticipantMonitoringCard className={classes.card} />}
        ref={scrollComponent}
      >
        {isOver &&
          canDrop &&
          itemType &&
          !containingCardTypes.includes(itemType) &&
          item &&
          (!pages ||
            !pages.length ||
            !pages[0].data.find(({ id }) => id === item.id)) && (
            <ParticipantMonitoringCard
              ghost
              className={classes.card}
              participation={item.participation}
            />
          )}
        {informationCard}
        {!count &&
          i18n.exists(`sessions.details.monitoring.empty.${status}`) && (
            <div className={classes.emptyBlock}>
              <Trans
                i18nKey={`sessions.details.monitoring.empty.${status}`}
                context={context}
              />
            </div>
          )}
        {pages.map(({ data }) =>
          data.map((part) => (
            <DraggableParticipantMonitoringCard
              participation={part}
              key={part.id}
              className={classes.card}
              onClick={onParticipationClick}
              dropOnColumn={canDrop && isOver}
              draggable
              quotaTags={
                quotasPerApplication?.get(part.mainApplicationId ?? '') || []
              }
            />
          )),
        )}
      </InfiniteLoadingContainer>
      {!!count && !scrollAtBottom && (
        <div className={classes.closingCardsBorder} />
      )}
    </div>
  );
});
